import type { VersionDrift } from '@/types/VersionDrift';
import parseVersion from '@/utils/parseVersion';

/**
 * Measures how far the live API has moved past the baseline this build was cut
 * for. Both arguments are API versions — the client version is unrelated and is
 * never compared against them.
 *
 * A patch bump leaves the contract intact and drifts by 'none'. A minor bump
 * breaks parts of the app, so refreshing would be nice. A major bump breaks the
 * app or one of its core features, so refreshing is required.
 *
 * An unreadable version, or an API that is behind the baseline (a rollout that
 * has not finished, or a rollback), drifts by 'none' — there is nothing a
 * refresh would fix.
 */
export default function (
  baselineVersion: string | undefined | null,
  liveVersion: string | undefined | null,
): VersionDrift {
  const baseline = parseVersion(baselineVersion);
  const live = parseVersion(liveVersion);
  if (!baseline || !live) return 'none';

  if (live.major !== baseline.major) return live.major > baseline.major ? 'major' : 'none';
  if (live.minor > baseline.minor) return 'minor';

  return 'none';
}
